import {computed, decorate, observable} from 'mobx';
import moment from 'moment';
import i18n from '../i18n/i18n';
import {BaseModel} from './base';
import {displayableProperty} from './displayableProperty';
import {displayablePropertyParam} from './displayablePropertyParam';
import {transformation} from '../utils/transformations';
import {datetimeFormat} from '../config/moment';
import appConfig from '../utils/appConfig';
import {sortChronologically} from '../components/shared/tables/sorters';

export class OperationStateLog extends BaseModel {
  populateAttributesFromStore(rootStore) {
    this.state = rootStore.operationStateStore.getById(this.stateId);
    this.interruptionReason = rootStore.interruptionReasonStore.getById(this.interruptionReasonId);
    this.operation = rootStore.operationStore.getById(this.operationId);
    this.createdByUser = rootStore.userStore.getById(this.createdBy);

    this.active = !this.end;

    return super.populateAttributesFromStore(rootStore);
  }

  /**
   * Calculates the duration of this operationStateLog and returns it as a moment.Duration object
   * @return {null|moment.Duration}
   */
  get durationSeconds() {
    if (this.start) {
      return moment.duration(
        moment((this.end) ? this.end : moment())
          .diff(moment(this.start), 'seconds'), 'seconds'
      );
    }
    return null;
  }

  displayableProperties = [
    displayableProperty({
      title: i18n.t('operationStateLog.model.attributes.start'),
      params: [
        displayablePropertyParam({
          path: 'start', transform: transformation.datetime({format: datetimeFormat}),
        }),
      ],
      template: '{value}',
      key: 'start',
      sorter: (a, b) => sortChronologically(a.start, b.start),
      raw: false,
    }),
    displayableProperty({
      title: i18n.t('operationStateLog.model.attributes.durationSeconds'),
      params: [
        displayablePropertyParam({
          path: 'durationSeconds', transform: transformation.detailedDuration,
        }),
      ],
      template: '{value}',
      key: 'durationSeconds',
      sorter: (a, b) => sortChronologically(a.durationSeconds, b.durationSeconds),
      raw: false,
    }),
    displayableProperty({
      title: i18n.t('operationStateLog.model.attributes.active'),
      params: [
        displayablePropertyParam({
          path: 'active', transform: transformation.none,
        }),
      ],
      template: '{value}',
      key: 'active',
      raw: false,
    }),
  ];

  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    const allDisplayableProperties = super.allDisplayableProperties(rootStore, pathPrefix, titlePrefix);
    Object.keys(appConfig.interruptionTags).forEach((key) => {
      const propertyKey = `tags.${key}`;
      allDisplayableProperties.push(
        displayableProperty({
          title: appConfig.interruptionTags[key],
          params: [
            displayablePropertyParam({
              path: propertyKey, transform: transformation.fallback(),
            }),
          ],
          template: '{value}',
          key: propertyKey,
        })
      );
    });

    return allDisplayableProperties;
  }
}

decorate(OperationStateLog, {
  id: observable,
  operationId: observable,
  operation: observable,
  stateId: observable,
  state: observable,
  interruptionReasonId: observable,
  interruptionReason: observable,
  start: observable,
  end: observable,
  message: observable,
  createdBy: observable,
  createdByUser: observable,
  durationSeconds: computed,
  active: observable,
  tags: observable,
});
